import { useCallback, useMemo, useRef, useState, type Ref } from 'react';
import type { CanvasItem } from './types';
import {
  CAMERA_PRESETS,
  buildScene,
  clampPitch,
  fitCamera,
  normalizeYaw,
  renderScene,
  type BuildSceneOptions,
  type Camera3D,
  type Viewport,
} from './three-d';

/**
 * Logical drawing surface. The SVG scales itself to its container through the
 * viewBox, so the view never has to measure the DOM — which also means it
 * renders identically in a test, in a modal and on a projector.
 */
export const VIEWPORT_3D: Viewport = { width: 1000, height: 640 };

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;
/** Degrees of orbit per pixel dragged. */
const ORBIT_SENSITIVITY = 0.35;

export interface FloorPlan3DViewProps {
  items: CanvasItem[];
  room?: BuildSceneOptions['room'];
  fallbackWidthFt?: number;
  fallbackHeightFt?: number;
  showChairs?: boolean;
  showLabels?: boolean;
  showGrid?: boolean;
  showWalls?: boolean;
  wallHeightFt?: number;
  chairSides?: BuildSceneOptions['chairSides'];
  perspective?: boolean;
  /** Starting viewpoint; the user orbits from there. */
  initialYaw?: number;
  initialPitch?: number;
  className?: string;
  /** The drawing itself — for capturing the view as a picture (AI Render Studio). */
  svgRef?: Ref<SVGSVGElement>;
}

export function FloorPlan3DView({
  items,
  room,
  fallbackWidthFt,
  fallbackHeightFt,
  showChairs = true,
  showLabels = true,
  showGrid = true,
  showWalls = true,
  wallHeightFt = 3,
  chairSides = 'top-bottom',
  perspective = true,
  initialYaw = 0,
  initialPitch = 42,
  className = '',
  svgRef,
}: FloorPlan3DViewProps) {
  const [yaw, setYaw] = useState(() => normalizeYaw(initialYaw));
  const [pitch, setPitch] = useState(() => clampPitch(initialPitch));
  const [zoom, setZoom] = useState(1);
  const drag = useRef<{ x: number; y: number } | null>(null);

  const scene = useMemo(
    () =>
      buildScene({
        items,
        room,
        fallbackWidthFt,
        fallbackHeightFt,
        showChairs,
        showLabels,
        chairSides,
        gridFt: showGrid ? 5 : 0,
        // A room drawn with its ceiling height stands at that height.
        wallHeightFt: showWalls ? (room?.shape?.wallHeightFt ?? wallHeightFt) : 0,
      }),
    [
      items,
      room,
      fallbackWidthFt,
      fallbackHeightFt,
      showChairs,
      showLabels,
      chairSides,
      showGrid,
      showWalls,
      wallHeightFt,
    ],
  );

  const camera: Camera3D = useMemo(() => {
    const base = fitCamera(scene.bounds, VIEWPORT_3D, { yaw, pitch, perspective });
    return { ...base, pxPerFt: base.pxPerFt * zoom };
  }, [scene.bounds, yaw, pitch, zoom, perspective]);

  const rendered = useMemo(() => renderScene(scene, camera, VIEWPORT_3D), [scene, camera]);

  const startDrag = useCallback((x: number, y: number) => {
    drag.current = { x, y };
  }, []);

  const moveDrag = useCallback((x: number, y: number) => {
    if (!drag.current) return;
    const dx = x - drag.current.x;
    const dy = y - drag.current.y;
    // A synthetic event without coordinates would turn the camera to NaN and
    // blank the view; there is nothing to orbit by, so leave it where it is.
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    drag.current = { x, y };
    setYaw((prev) => normalizeYaw(prev - dx * ORBIT_SENSITIVITY));
    // Dragging up tilts the camera down onto the plan, the way pulling a model
    // toward you tips its far edge up.
    setPitch((prev) => clampPitch(prev - dy * ORBIT_SENSITIVITY));
  }, []);

  const endDrag = useCallback(() => {
    drag.current = null;
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<SVGSVGElement>) => {
      const touch = e.touches[0];
      if (touch) startDrag(touch.clientX, touch.clientY);
    },
    [startDrag],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<SVGSVGElement>) => {
      const touch = e.touches[0];
      if (touch) moveDrag(touch.clientX, touch.clientY);
    },
    [moveDrag],
  );

  const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
    setZoom((prev) => clampZoom(prev * (e.deltaY > 0 ? 0.9 : 1.1)));
  }, []);

  const applyPreset = useCallback((presetYaw: number, presetPitch: number) => {
    setYaw(normalizeYaw(presetYaw));
    setPitch(clampPitch(presetPitch));
    setZoom(1);
  }, []);

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid="floor-plan-3d">
      <div className="flex flex-wrap items-center gap-1.5">
        {CAMERA_PRESETS.map((preset) => {
          const active = Math.round(yaw) === preset.yaw && Math.round(pitch) === preset.pitch;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.yaw, preset.pitch)}
              className={`px-2.5 py-1 text-xs rounded border ${
                active
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`preset-${preset.id}`}
            >
              {preset.label}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setZoom((prev) => clampZoom(prev / 1.2))}
            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            data-testid="zoom-out-3d"
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="text-xs text-gray-500 tabular-nums" data-testid="zoom-level-3d">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((prev) => clampZoom(prev * 1.2))}
            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            data-testid="zoom-in-3d"
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEWPORT_3D.width} ${VIEWPORT_3D.height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full bg-gradient-to-b from-slate-100 to-slate-200 rounded-md border border-gray-200 touch-none cursor-grab active:cursor-grabbing"
        data-testid="floor-plan-3d-canvas"
        role="img"
        aria-label="3D view of the floor plan"
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={endDrag}
        onWheel={handleWheel}
      >
        <defs>
          <clipPath id="floor-plan-3d-floor-clip">
            <polygon points={rendered.floor} />
          </clipPath>
        </defs>

        <polygon
          points={rendered.floor}
          fill="#f8fafc"
          stroke="#94a3b8"
          strokeWidth={1.5}
          data-testid="floor-3d"
        />
        {showGrid && (
          <g clipPath="url(#floor-plan-3d-floor-clip)" data-testid="grid-3d">
            {rendered.grid.map((line) => (
              <line
                key={line.key}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="#cbd5e1"
                strokeWidth={0.6}
              />
            ))}
          </g>
        )}

        <g data-testid="solids-3d">
          {rendered.faces.map((face) => (
            <polygon
              key={face.key}
              points={face.points}
              fill={face.fill}
              stroke={face.stroke}
              strokeWidth={0.4}
              strokeLinejoin="round"
              opacity={face.opacity}
            />
          ))}
        </g>

        {showLabels && (
          <g data-testid="labels-3d">
            {rendered.labels.map((label) => (
              <g key={label.key}>
                <rect
                  x={label.x - Math.max(9, label.text.length * 3.6)}
                  y={label.y - 9}
                  width={Math.max(18, label.text.length * 7.2)}
                  height={16}
                  rx={8}
                  fill="#ffffff"
                  fillOpacity={0.85}
                  stroke="#cbd5e1"
                  strokeWidth={0.5}
                />
                <text
                  x={label.x}
                  y={label.y + 2}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#334155"
                  fontWeight={600}
                >
                  {label.text}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}
